import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { PROCESS_STEPS } from "@/data/content";

const STEP_DETAIL = {
    Discovery: "A focused conversation about your business, customers, competitors, and what success looks like. For a website this means audiences and content; for social media it means voice and platforms; for marketing it means goals and budgets.",
    Strategy: "We turn what we learned into a concrete plan: scope, deliverables, timeline, and investment — confirmed in writing before work begins.",
    Design: "Visual direction and creative concepts take shape: wireframes or full designs for websites, identity concepts for branding, content templates for social, creative variations for campaigns.",
    Development: "The approved direction gets built — a website coded and optimized, a brand system finalized, a content calendar produced, or campaigns configured. The word 'development' adapts to your service.",
    Review: "You review, we refine. Structured feedback rounds, testing on real devices, and quality checks before anything goes live.",
    Launch: "We deliver the approved project, hand over what you need to run it, and discuss ongoing support, maintenance, or growth programs.",
};

export default function Process() {
    return (
        <main className="pt-32 sm:pt-40 pb-24">
            <Seo title="Our Process" path="/process" description="The Innovative Fusion Hives client journey: Discovery, Strategy, Design, Development, Review, Launch — a clear process for every service." />
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="process-hero">
                <Reveal>
                    <Eyebrow>Our Process</Eyebrow>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                        Six steps. Zero surprises.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
                        A clear client journey from first conversation to launch — adapted to your service, whether that is a website, a brand identity, a social program, or a marketing campaign.
                    </p>
                </Reveal>
            </section>

            <section className="mx-auto max-w-4xl px-4 sm:px-8 mt-20" data-testid="process-timeline">
                <div className="relative border-l border-white/10 pl-8 sm:pl-12 space-y-14">
                    {PROCESS_STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.05}>
                            <div className="relative" data-testid={`process-step-${s.name.toLowerCase()}`}>
                                <span className="absolute -left-[41px] sm:-left-[57px] top-0 flex h-8 w-8 items-center justify-center rounded-full border border-violet-500/50 bg-[#0E1320] font-mono2 text-[10px] text-violet-300">
                                    {s.n}
                                </span>
                                <h2 className="font-display text-2xl font-bold text-white">{s.name}</h2>
                                <p className="mt-2 text-sm font-semibold text-slate-300">{s.text}</p>
                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{STEP_DETAIL[s.name]}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-24" data-testid="process-cta">
                <Reveal>
                    <div className="rounded-2xl border border-violet-500/25 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(124,58,237,0.18),transparent_75%)] bg-hive-card p-10 sm:p-14 text-center">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Step one starts with a message.</h2>
                        <p className="mx-auto mt-4 max-w-xl text-sm text-slate-400">Tell us about your goals and we will schedule a Discovery conversation.</p>
                        <Link to="/contact" data-testid="process-cta-button"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-violet-600 px-8 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 hover:shadow-[0_0_32px_rgba(124,58,237,0.5)]">
                            Start Your Project <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
